import { OrderConstant } from 'src/constants';
import { OrderDto, OrderStatusIntDto, OrderTypeIntDto } from 'src/dtos';
import { Order, OrderAction, OrderDuration, OrderStatus, OrderType, Quote } from 'src/models';

const incompleteMarketOrderPrice = (order: OrderDto, lastValue: number = 0): number => {
  let price = lastValue;

  if (
    order.OrderStatus === OrderStatusIntDto.Cancelled ||
    order.OrderStatus === OrderStatusIntDto.Rejected ||
    order.OrderStatus === OrderStatusIntDto.Expired
  ) {
    price = 0; // NOTE: TB market order price is not saved for unfilled orders;
  }

  return price;
};

const calculatePrice = (order: OrderDto, lastValue: number = 0): number => {
  if (order.AverageFillPrice !== undefined && order.AverageFillPrice > 0) {
    return order.AverageFillPrice;
  }

  if (order.OrderStatus === OrderStatusIntDto.Filled) {
    return order.AverageFillPrice ?? 0;
  }

  try {
    switch (order.OrderType) {
      case OrderTypeIntDto.Market: {
        return incompleteMarketOrderPrice(order, lastValue);
      }

      case OrderTypeIntDto.Limit: {
        return order.Price ?? 0;
      }

      case OrderTypeIntDto.Stop_Market: {
        return order.Stop ?? 0;
      }

      case OrderTypeIntDto.Stop_Limit: {
        return order.Price ?? 0;
      }

      case OrderTypeIntDto.Market_On_Close: {
        return incompleteMarketOrderPrice(order, lastValue);
      }

      default: {
        throw new Error(`Expected order type "OrderTypeIntDto". Received: "${order?.OrderType}"`);
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);

    return 0;
  }
};

const isPartial = (dto: OrderDto): boolean =>
  Boolean(dto.FillQuantity && dto.FillQuantity < dto.Quantity && dto.FillQuantity > 0);

const addTotalCostToPartialOrder = (order: Order): Order => {
  let totalCost = order.filledQuantity * order.price;

  if (order.action?.isBuy) {
    totalCost = -(totalCost + order.commission);
  }

  if (order.action?.isSell) {
    totalCost = totalCost - order.commission;
  }

  return { ...order, totalCost };
};

const addTotalCostToCompletedOrder = (order: Order): Order => {
  let totalCost = order.quantity * order.price;

  if (order.action?.isBuy) {
    totalCost = -(totalCost + order.commission);
  }

  if (order.action?.isSell) {
    totalCost = totalCost - order.commission;
  }

  return { ...order, totalCost };
};

const addTotalCostToIncompleteOrder = (order: Order): Order => {
  let totalCost = order.quantity * order.price;

  if (order.isPartial) {
    return addTotalCostToPartialOrder(order);
  }

  if (order.action?.isBuy) {
    totalCost = -(totalCost + OrderConstant.ESTIMATED_SERVICE_FEE);
  }

  if (order.action?.isSell) {
    totalCost = totalCost - OrderConstant.ESTIMATED_SERVICE_FEE;
  }

  return { ...order, totalCost };
};

const addTotalCost = (order: Order): Order => {
  let totalCost = order.quantity * order.price;

  if (totalCost === 0) {
    return order;
  }

  return order.status.isCompleted ? addTotalCostToCompletedOrder(order) : addTotalCostToIncompleteOrder(order);
};

const addCommission = (order: Order): Order => {
  if (order.status.isCompleted || order.isPartial) {
    return order;
  }

  return { ...order, commission: OrderConstant.ESTIMATED_SERVICE_FEE };
};

export const mapOrderDtoToModel = (dto: OrderDto, quote?: Quote): Order => {
  let result: Order = {
    id: dto.OrderId.toString(),
    updatedAt: dto.Date,
    quantity: dto.Quantity,
    filledQuantity: dto.FillQuantity ?? 0,
    isPartial: isPartial(dto),
    price: calculatePrice(dto, quote?.last),
    totalCost: 0,
    type: new OrderType(dto.OrderType),
    symbol: dto.UnderlyingSymbol ?? '',
    description: quote?.description ?? dto.UnderlyingSymbol ?? '',
    action: new OrderAction(dto.Legs?.[0]?.Action),
    status: new OrderStatus(dto.OrderStatus),
    duration: new OrderDuration(dto.Duration),
    commission: dto.CommissionAssessed,
    limit: dto.Price,
    stop: dto.Stop,
  };
  result = addTotalCost(result);
  result = addCommission(result);

  return result;
};

export const mapGetOrderListResponseDtoToModel = (dtos: OrderDto[], quotes: Quote[]): Order[] =>
  dtos.map(orderDto =>
    mapOrderDtoToModel(
      orderDto,
      quotes.find(aQuote => aQuote.symbol === orderDto.UnderlyingSymbol),
    ),
  );
